import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  IconButton,
  Tooltip,
  Button
} from '@mui/material';
import {
  Close as CloseIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import { FileViewer, getFileType, type FileType } from './FileViewer';
import { lessonPlanService } from '../services/lessonPlanService';
import type { LessonPlan } from '../types';
import { logger } from '../utils/logger';
// @ts-ignore
import html2pdf from 'html2pdf.js';


interface LessonPlanFileViewerProps {
  lessonPlan: LessonPlan | null;
  open: boolean;
  onClose: () => void;
  onDownload?: (lessonPlan: LessonPlan) => void;
  canApprove?: boolean;
  onApprove?: (lessonPlan: LessonPlan) => void;
  onReview?: (lessonPlan: LessonPlan) => void;
  onReject?: (lessonPlan: LessonPlan) => void;
}

export const LessonPlanFileViewer: React.FC<LessonPlanFileViewerProps> = ({
  lessonPlan,
  open,
  onClose,
  onDownload,
  canApprove,
  onApprove,
  onReview,
  onReject
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fileUrl, setFileUrl] = useState<string | null>(null);
  const [fileType, setFileType] = useState<FileType>('unsupported');
  const [currentPage, setCurrentPage] = useState(0);
  const [numPages, setNumPages] = useState<number | null>(null);

  useEffect(() => {
    if (open && lessonPlan) {
      loadFile();
    } else {
      // Cleanup when closing
      if (fileUrl && fileUrl.startsWith('blob:')) {
        URL.revokeObjectURL(fileUrl);
      }
      setFileUrl(null);
      setCurrentPage(0);
      setNumPages(null);
    }
  }, [open, lessonPlan]);

  const loadFile = async () => {
    if (!lessonPlan) return;

    setLoading(true);
    setError(null);

    try {
      // Handle digital lesson plans
      if (lessonPlan.is_digital) {
        setFileType('html');
        const content = lessonPlan.content || '<p>Không có nội dung</p>';
        const blob = new Blob([content], { type: 'text/html' });
        const url = URL.createObjectURL(blob);
        setFileUrl(url);
        setLoading(false);
        return;
      }

      // Get file extension and determine file type
      const fileName = lessonPlan.fileName || '';
      const detectedFileType = getFileType(fileName);
      setFileType(detectedFileType);

      // In-app viewer: use authenticated download endpoint to get Blob, then show via blob URL.
      // This avoids calling /public/:id/file which can't include x-school-code in iframe/img/video tags.
      if (detectedFileType === 'office') {
        // Office Online Viewer needs a publicly accessible URL (cannot use blob/localhost reliably)
        // Fallback: allow download instead of inline preview.
        setError('File Office này không hỗ trợ xem trực tiếp trong hệ thống. Vui lòng tải về để xem.');
        setLoading(false);
        return;
      }

      const blob = await lessonPlanService.downloadLessonPlan(lessonPlan.id);
      const url = URL.createObjectURL(blob);
      setFileUrl(url);
      setCurrentPage(0);
      setLoading(false);
    } catch (err) {
      logger.error('Error loading lesson plan file:', err);
      const errorMessage = err instanceof Error ? err.message : 'Không thể tải file giáo án';
      setError(errorMessage);
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (fileUrl && fileUrl.startsWith('blob:')) {
      URL.revokeObjectURL(fileUrl);
    }
    setFileUrl(null);
    setError(null);
    setCurrentPage(0);
    setNumPages(null);
    onClose();
  };

  const handleDownload = async () => {
    if (!lessonPlan) return;
    try {
      if (lessonPlan.is_digital) {
        const content = lessonPlan.content || '<p>Không có nội dung</p>';

        // Create a temporary container for PDF generation
        const element = document.createElement('div');
        element.innerHTML = `
          <div style="font-family: Arial, sans-serif; padding: 20px;">
            <h1 style="text-align: center; color: #333; margin-bottom: 20px;">${lessonPlan.title}</h1>
            <div style="margin-bottom: 30px;">
              <p><strong>Giáo viên:</strong> ${lessonPlan.submittedByUser?.fullName || 'N/A'}</p>
              <p><strong>Môn học:</strong> ${lessonPlan.subject}</p>
              <p><strong>Tuần:</strong> ${lessonPlan.week}</p>
            </div>
            <hr style="border: 1px solid #eee; margin-bottom: 30px;" />
            <div class="content">
              ${content}
            </div>
          </div>
        `;

        const opt = {
          margin: 10,
          filename: `${lessonPlan.title}.pdf`,
          image: { type: 'jpeg' as const, quality: 0.98 },
          html2canvas: { scale: 2, useCORS: true },
          jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' as const }
        };

        // Generate and save
        html2pdf().set(opt).from(element).save();
        // Note: onDownload is just a notification, not a download trigger
        return;
      }

      await lessonPlanService.downloadLessonPlanFileDirectly(lessonPlan);
      // Note: onDownload is just a notification, not a download trigger
    } catch (err) {
      logger.error('Error downloading lesson plan:', err);
    }
  };

  if (!lessonPlan) return null;

  const isPending = lessonPlan.status === 'Pending';
  const isUnderReview = lessonPlan.status === 'UnderReview';

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={false}
      fullWidth
      PaperProps={{
        sx: {
          width: '100vw',
          height: '100vh',
          maxWidth: '100vw',
          maxHeight: '100vh',
          m: 0,
          borderRadius: 0
        }
      }}
    >
      <DialogContent
        sx={{
          p: 0,
          position: 'relative',
          height: '100%',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: '#000'
        }}
      >
        {/* Header bar */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: 1.5,
            zIndex: 1000,
            gap: 2
          }}
        >
          <Typography variant="h6" sx={{ color: 'white', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {lessonPlan.title}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            {/* Action Buttons */}
            {canApprove && (isPending || isUnderReview) && (
              <>
                {isPending && onReview && (
                  <Button
                    variant="contained"
                    color="info"
                    size="small"
                    onClick={() => onReview(lessonPlan)}
                    sx={{ mr: 1 }}
                  >
                    Đang xem xét
                  </Button>
                )}
                {onReject && (
                  <Button
                    variant="contained"
                    color="warning"
                    size="small"
                    onClick={() => onReject(lessonPlan)}
                    sx={{ mr: 1 }}
                  >
                    Yêu cầu chỉnh sửa
                  </Button>
                )}
                {onApprove && (
                  <Button
                    variant="contained"
                    color="success"
                    size="small"
                    onClick={() => onApprove(lessonPlan)}
                    sx={{ mr: 1 }}
                  >
                    Duyệt
                  </Button>
                )}
              </>
            )}

            {fileType === 'pdf' && numPages && (
              <Typography variant="body2" sx={{ color: 'white', mr: 1 }}>
                Trang {currentPage + 1} / {numPages}
              </Typography>
            )}
            <Tooltip title="Tải xuống">
              <IconButton onClick={handleDownload} sx={{ color: 'white' }} size="small">
                <DownloadIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Đóng">
              <IconButton onClick={handleClose} sx={{ color: 'white' }} size="small">
                <CloseIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Content area - Use FileViewer component */}
        <Box
          sx={{
            flex: 1,
            position: 'relative',
            overflow: 'hidden',
            mt: 7, // Space for header
          }}
        >
          <FileViewer
            fileUrl={fileUrl}
            fileName={lessonPlan.fileName || lessonPlan.title}
            fileType={fileType}
            loading={loading}
            error={error}
            onPageChange={setCurrentPage}
            onNumPagesChange={setNumPages}
            currentPage={currentPage}
            numPages={numPages}
            showNavigation={true}
            fullHeight={true}
          />
        </Box>
      </DialogContent>
    </Dialog>
  );
};

